import React, { useState, useEffect } from 'react';
import { FaFileAlt, FaDownload, FaChevronUp, FaChevronDown, FaChartLine, FaEye, FaShieldAlt, FaRocket, FaPlay } from 'react-icons/fa';
import './Resources.css';
import Footer from '../../components/Footer';
import victaaLogo from '../../assets/Resources/image.png';
import riskavaireIcon from '../../assets/Resources/resources-riskavaire.svg';

const Resources = () => {
  const [expandedProducts, setExpandedProducts] = useState({
    'RiskaVaire': true,
    'ViCTAA': true
  });

  // Preload logo images immediately for instant display
  useEffect(() => {
    const logos = [victaaLogo, riskavaireIcon];
    logos.forEach(logo => {
      if (logo) {
        const img = new Image();
        img.src = logo;
        if (img.decode) {
          img.decode().catch(() => {});
        }
      }
    });
  }, []);

  const toggleProduct = (product) => {
    setExpandedProducts(prev => ({
      ...prev,
      [product]: !prev[product]
    }));
  };

  const resourcesData = {
    'RiskaVaire': {
      icon: <img 
        src={riskavaireIcon} 
        alt="RiskaVaire Logo" 
        className="resources-riskavaire-logo"
        loading="eager"
        fetchPriority="high"
        decoding="async"
      />,
      description: 'Comprehensive suite for managing governance, risk, and compliance across enterprise environments.',
      totalResources: 3,
      gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
      resources: [
        {
          title: 'RiskaVaire Product Brief',
          type: 'PDF',
          size: '151 KB',
          description: 'Complete overview of RiskaVaire features, benefits, and implementation options.',
          icon: <FaFileAlt />,
          filePath: '/assets/resources/riskavaire/RiskaVaire Product Brief.pdf',
          fileName: 'RiskaVaire-Product-Brief.pdf'
        },
        {
          title: 'RiskaVaire Booklet',
          type: 'PDF',
          size: '2 MB',
          description: 'Comprehensive RiskaVaire booklet with detailed information.',
          icon: <FaFileAlt />,
          filePath: '/assets/resources/riskavaire/RiskaVaire Booklet.pdf',
          fileName: 'RiskaVaire-Booklet.pdf'
        },
        {
          title: 'RiskaVaire Playbook',
          type: 'PDF',
          size: '1.5 MB',
          description: 'RiskaVaire playbook for implementation and best practices.',
          icon: <FaFileAlt />,
          filePath: '/assets/resources/riskavaire/RiskaVaire Playbook.pdf',
          fileName: 'RiskaVaire-Playbook.pdf'
        }
      ]
    },
    'ViCTAA': {
      icon: <img 
        src={victaaLogo} 
        alt="ViCTAA Logo" 
        className="resources-victaa-logo"
        loading="eager"
        fetchPriority="high"
        decoding="async"
      />,
      description: 'Comprehensive suite of resources to protect all your endpoints.',
      totalResources: 2,
      gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      resources: [
        {
          title: 'ViCTAA Product Brief',
          type: 'PDF',
          size: '151 KB',
          description: 'Complete overview of ViCTAA features, benefits, and implementation options.',
          icon: <FaFileAlt />,
          filePath: '/assets/resources/victaa/ViCTAA Product Brief.pdf',
          fileName: 'ViCTAA-Product-Brief.pdf'
        },
        {
          title: 'ViCTAA Booklet',
          type: 'PDF',
          size: '2 MB',
          description: 'Comprehensive ViCTAA booklet with detailed information.',
          icon: <FaFileAlt />,
          filePath: '/assets/resources/victaa/Victaa Booklet.pdf',
          fileName: 'ViCTAA-Booklet.pdf'
        },
        {
          title: 'ViCTAA Admin User Manual',
          type: 'PDF',
          size: '7.3 MB',
          description: 'Comprehensive admin user manual for ViCTAA platform.',
          icon: <FaFileAlt />,
          filePath: '/assets/resources/victaa/Admin-User-Manual.pdf',
          fileName: 'ViCTAA-Admin-User-Manual.pdf'
        },
        {
          title: 'ViCTAA Non-Admin User Manual',
          type: 'PDF',
          size: '824 KB',
          description: 'User manual for non-admin users of ViCTAA platform.',
          icon: <FaFileAlt />,
          filePath: '/assets/resources/victaa/Non-Admin User Manual.pdf',
          fileName: 'ViCTAA-Non-Admin-User-Manual.pdf'
        },
        {
          title: 'ViCTAA Operational Handbook',
          type: 'PDF',
          size: '346 KB',
          description: 'Operational handbook for ViCTAA platform usage.',
          icon: <FaFileAlt />,
          filePath: '/assets/resources/victaa/ViCTAA-Operational Handbook.pdf',
          fileName: 'ViCTAA-Operational-Handbook.pdf'
        },
        {
          title: 'ViCTAA FAQ Pack',
          type: 'PDF',
          size: '94 KB',
          description: 'Frequently asked questions and answers for ViCTAA.',
          icon: <FaFileAlt />,
          filePath: '/assets/resources/victaa/ViCTAA FAQ Pack.pdf',
          fileName: 'ViCTAA-FAQ-Pack.pdf'
        },
        {
          title: 'ViCTAA End User License Agreement',
          type: 'PDF',
          size: '116 KB',
          description: 'End user license agreement for ViCTAA platform.',
          icon: <FaFileAlt />,
          filePath: '/assets/resources/victaa/ViCTAA END USER LICENSE AGREEMENT.pdf',
          fileName: 'ViCTAA-End-User-License-Agreement.pdf'
        },
        {
          title: 'ViCTAA Launch Video',
          type: 'MP4',
          size: '26 MB',
          description: 'Launch video demonstration of ViCTAA platform.',
          icon: <FaPlay />,
          filePath: 'https://www.linkedin.com/posts/vardaan-data-sciences-pvt-ltd_endpointsecurity-cybersecurity-techforgood-activity-7355954657745137666-AHiF?utm_source=share&utm_medium=member_desktop&rcm=ACoAAA0pAHYBdnc3ZD55x6wEPtpcp5CwjlHdmCY',
          fileName: 'ViCTAA-Launch-Video.mp4',
          isExternalLink: true
        }
      ]
    }
  };

  const handleAction = (resource) => {
    if (resource.isExternalLink) {
      // Open external link in new tab
      window.open(resource.filePath, '_blank');
      console.log(`Opening ${resource.title} in new tab`);
    } else {
      try {
        // Create a temporary link element for download
        const link = document.createElement('a');
        link.href = resource.filePath;
        link.download = resource.fileName;
        link.target = '_blank';
        
        // Append to body, click, and remove
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        console.log(`Downloading ${resource.title}`);
      } catch (error) {
        console.error('Download failed:', error);
        // Fallback: open in new tab
        window.open(resource.filePath, '_blank');
      }
    }
  };

  return (
    <div className="resources-page">
      {/* Header Section */}
      <header className="resources-header">
        <div className="resources-header-bg">
          <div className="resources-header-grid"></div>
          <div className="resources-header-dots"></div>
        </div>
        <div className="resources-header-content">
          <div className="resources-header-icon">
            <FaRocket />
          </div>
          <h1 className="resources-header-title">Product Resources</h1>
          <p className="resources-header-subtitle">
            Access comprehensive documentation and guides for all our products.
          </p>
        </div>
      </header>

      {/* Content Section */}
      <main className="resources-content">
        <div className="resources-container">
          {/* Resource Cards */}
          {Object.entries(resourcesData).map(([productName, productData]) => (
            <div key={productName} className="resource-product-card">
              {/* Product Header */}
              <div className="resource-product-header">
                <div className="resource-product-info">
                  <div className="resource-product-icon victaa-icon">
                    {productData.icon}
                  </div>
                  <div className="resource-product-details">
                    <p className="resource-product-description">{productData.description}</p>
                  </div>
                </div>
                <button 
                  className="resource-expand-btn"
                  onClick={() => toggleProduct(productName)}
                >
                  {expandedProducts[productName] ? <FaChevronUp /> : <FaChevronDown />}
                </button>
              </div>

              {/* Product Content */}
              {expandedProducts[productName] && (
                <>
                  {productData.comingSoon ? (
                    <div className="coming-soon-section">
                      <div className="coming-soon-content">
                        <div className="coming-soon-icon" style={{ background: productData.gradient }}>
                          <FaShieldAlt />
                        </div>
                        <h3 className="coming-soon-title">Coming Soon</h3>
                        <p className="coming-soon-description">
                          Resources for {productName} will be available soon.
                        </p>
                        <div className="coming-soon-progress">
                          <div className="progress-bar">
                            <div className="progress-fill"></div>
                          </div>
                          <span className="progress-text">In Progress</span>
                        </div>
                      </div>
                    </div>
                  ) : (
                    /* Direct Resources Display */
                    <div className="resource-cards-grid">
                      {productData.resources
                        .filter((resource) => {
                          if (productName === 'ViCTAA') {
                            return resource.title === 'ViCTAA Product Brief' || resource.title === 'ViCTAA Booklet';
                          }
                          return true; // Show all resources for other products
                        })
                        .map((resource, index) => (
                        <div key={index} className="resource-card">
                          <div className="resource-card-header">
                            <div className={`resource-card-icon ${resource.isExternalLink ? 'view-icon' : ''}`}>
                              {resource.icon}
                            </div>
                            <div className="resource-card-info">
                              <h4 className="resource-card-title">{resource.title}</h4>
                              <div className="resource-card-meta">
                                <span className="resource-card-type">{resource.type}</span>
                                <span className="resource-card-size">{resource.size}</span>
                              </div>
                            </div>
                          </div>
                          <button 
                            className={`resource-download-btn ${resource.isExternalLink ? 'view-btn' : ''}`}
                            onClick={() => handleAction(resource)}
                          >
                            {resource.isExternalLink ? <FaPlay /> : <FaDownload />}
                            <span>{resource.isExternalLink ? 'View' : 'Download'}</span>
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          ))}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Resources;